/* eslint-disable react-hooks/exhaustive-deps */
import Loading from "components/Loading";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import EconomicInvestigationForm from "pages/Incident/IncidentDetail/modals/EconomicInvestigationForm";
import InvestigationFileForm from "pages/InvestigationFileList/modal/InvestigationFileForm";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaBriefcase } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  CrimeReference,
  CrimeType,
  FILE_URL,
  Investigation,
  InvestigationStatus,
  InvestigationStatus2,
  Roles,
} from "types";
import { ITEMS_PER_PAGE } from "utils/constant";
import {
  constructFileUrl,
  DateView,
  errorHandler,
  hasRole,
} from "utils/helper";
import PageHeader from "../../components/PageHeader";
import defaultAvatar from "../../images/avatar.png";
import {
  fetchCrimeReferences,
  fetchInvestigation,
} from "../../services/incidentService";

const InvestigationList: React.FC = () => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const tabs = [
    t("all"),
    t("has_e_file"),
    t("no_e_file"),
    t("economic_or_corruption"),
  ];
  const [activeTab, setActiveTab] = useState(t("all"));

  const [investigations, setInvestigations] = useState<Investigation[]>([]);
  const [incidents, setIncidents] = useState<CrimeReference[]>([]);

  const [action, setAction] = useState<"Add" | "Edit">("Add");

  const isPolice = loginData?.role === "Member" || loginData?.is_police;

  useEffect(() => {
    isPolice && setActiveTab(t("economic_or_corruption"));
  }, [loginData, t]);

  const [loading, setLoading] = useState(true);
  const [isOpenForm, setIsOpenForm] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<CrimeReference>();

  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    fetchInvestigation()
      .then((data) => {
        setInvestigations(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });

    fetchCrimeReferences().then((data) => {
      setIncidents(data || []);
      setLoading(false);
    });
  };
  useEffect(() => {
    fetchData();
  }, []);

  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const searchTerm = search.trim().toLowerCase();
  const filteredInvestigations = investigations.filter((investigation) => {
    const prosecutor =
      investigation?.prosecutor?.full_name?.toLowerCase() || "";
    const police = investigation?.police?.full_name?.toLowerCase() || "";
    const crime_type =
      investigation?.crime_reference?.crime_type?.toString()?.toLowerCase() ||
      "";
    const status =
      investigation?.crime_reference?.status?.toString()?.toLowerCase() || "";
    const start_date = investigation.start_date
      ? new Date(investigation.start_date).toLocaleDateString().toLowerCase()
      : "";
    const end_date = investigation.end_date
      ? new Date(investigation.end_date).toLocaleDateString().toLowerCase()
      : "";
    const Incident_Registration_number =
      investigation?.crime_reference?.Incident_Registration_number?.toLowerCase() ||
      "";

    const matchesText =
      prosecutor.includes(searchTerm) ||
      police.includes(searchTerm) ||
      crime_type.includes(searchTerm) ||
      status.includes(searchTerm) ||
      start_date.includes(searchTerm) ||
      end_date.includes(searchTerm) ||
      status.includes(searchTerm) ||
      Incident_Registration_number.includes(searchTerm);

    if (activeTab === t("has_e_file")) {
      return matchesText && investigation?.crime_reference?.is_efling === true;
    } else if (activeTab === t("no_e_file")) {
      return matchesText && investigation?.crime_reference?.is_efling !== true;
    } else if (activeTab === t("economic_or_corruption")) {
      return matchesText && investigation?.ec_police_station_name;
    }

    return matchesText;
  });

  const [currentPage, setCurrentPage] = useState(1);

  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const paginatedInvestigations = filteredInvestigations.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredInvestigations.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
      <PageHeader
        title={t("investigation")}
        icon={FaBriefcase}
        tabs={isPolice ? tabs.slice(3) : tabs}
        activeTab={activeTab}
        onTabClick={setActiveTab}
        rightContent={<></>}
      />

      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {loading ? (
            <Loading label={t("loading_incidents")} />
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="text-white bg-black">
                <tr>
                  <th colSpan={7} className="p-4" rounded-t-lg>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-lg font-semibold">
                        <button
                          onClick={() => navigate(-1)}
                          className="text-white hover:text-gray-300"
                          title={t("go_back")}
                        >
                          ◀️
                        </button>
                        <span>{t("incidents_list_table")}</span>
                      </div>

                      <div className="relative w-80">
                        <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                          🔍
                        </span>
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          placeholder={t("search")}
                          className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th className="p-3 text-left">
                    {activeTab === t("economic_or_corruption")
                      ? t("police_station")
                      : t("prosecutor")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === t("economic_or_corruption")
                      ? t("sent_date_to_police")
                      : t("police")}
                  </th>
                  <th className="p-3 text-left">
                    {activeTab === t("economic_or_corruption")
                      ? t("sent_date_to_coordinator")
                      : t("date")}
                  </th>
                  <th className="p-3 text-left">{t("status")}</th>
                  {activeTab !== t("economic_or_corruption") && (
                    <th className="p-3 text-left">{t("cr_number")}</th>
                  )}
                  <th className="p-3 text-left">{t("crime_type")}</th>
                  <th className="p-3 text-center" rounded-tr-lg>
                    {t("detail")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedInvestigations.map((investigation, idx) => (
                  <tr
                    key={idx + indexOfFirstItem}
                    className={idx % 2 === 0 ? "bg-[#F7F4F1AB]" : "bg-white"}
                  >
                    <td className="p-3 font-[500] font-[Poppins]">
                      {activeTab === t("economic_or_corruption") ? (
                        investigation?.ec_police_station_name
                      ) : (
                        <div className="flex items-center gap-1">
                          <img
                            src={defaultAvatar}
                            alt="avatar"
                            className="w-[40] h-[40] rounded-full"
                          />
                          <div className="ml-2">
                            <div className="font-base font-[Poppins]">
                              {investigation?.prosecutor?.full_name}
                            </div>
                            <div className="font-xs text-xs font-[Poppins]">
                              {t("prosecutor")}
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {activeTab === t("economic_or_corruption") ? (
                        DateView(investigation?.sent_date_to_police)
                      ) : (
                        <div className="flex items-center gap-1">
                          <img
                            src={defaultAvatar}
                            alt="avatar"
                            className="w-[40] h-[40] rounded-full"
                          />
                          <div className="ml-2">
                            <div className="font-base font-[Poppins]">
                              {investigation?.police?.full_name}
                            </div>
                            <div className="font-xs text-xs font-[Poppins]">
                              {t("police")}
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      {activeTab === t("economic_or_corruption")
                        ? DateView(investigation?.sent_back_to_cordinator) ||
                          "N/A"
                        : DateView(investigation.start_date) +
                          " - " +
                          DateView(investigation.end_date)}
                    </td>
                    <td className="p-3 font-[500] font-[inter]">
                      <StatusBadge
                        status={
                          activeTab === t("economic_or_corruption")
                            ? investigation?.status
                              ? InvestigationStatus2[investigation.status]
                              : ""
                            : investigation?.investigation_status != null
                              ? InvestigationStatus[
                                  investigation.investigation_status
                                ]
                              : ""
                        }
                      />
                    </td>
                    {activeTab !== t("economic_or_corruption") && (
                      <td className="p-3 font-[500] font-[Poppins]">
                        {investigation?.CR_number}
                      </td>
                    )}
                    <td className="p-3 font-[500] font-[Poppins]">
                      {investigation?.crime_reference?.crime_type
                        ? CrimeType[investigation?.crime_reference?.crime_type]
                        : ""}
                    </td>
                    <td className="p-3 font-[500] font-[Poppins]">
                      <div className="pt-2 pl-[55px] flex gap-2">
                        {activeTab === t("economic_or_corruption") && (
                          <a
                            href={(() => {
                              const doc = investigation?.documents?.find(
                                (doc) =>
                                  doc.document_type ===
                                  "ec_reference_result_file",
                              );
                              return doc
                                ? constructFileUrl(doc, FILE_URL.INVESTIGATION)
                                : "#";
                            })()}
                            target="_blank"
                            key={investigation?.uu_id}
                            className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                            rel="noreferrer"
                          >
                            {t("reference_result_file")}
                          </a>
                        )}
                        {isPolice &&
                          activeTab === t("economic_or_corruption") && (
                            <button
                              type="button"
                              onClick={() => {
                                setAction("Add");
                                setIsOpenForm(true);
                                setSelectedIncident(
                                  investigation?.crime_reference ?? undefined,
                                );
                              }}
                              className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                            >
                              {t("add_investigation_file")}
                            </button>
                          )}

                        <button
                          type="button"
                          onClick={() =>
                            navigate(
                              "/incident/" + investigation?.crime_reference,
                              { state: { tab: "Investigation" } },
                            )
                          }
                          className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                        >
                          {t("view")}
                        </button>
                        {isCoordinator && (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                "/incident/" + investigation?.crime_reference,
                                { state: { tab: "Investigation" } },
                              )
                            }
                            className="px-4 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300"
                          >
                            {t("edit")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredInvestigations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {t("no_investigation_found")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-4">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("prev")}
            </button>
            <span className="px-2">
              {t("page")} {currentPage} {t("of")} {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              {t("next")}
            </button>
          </div>
        )}
      </div>

      {isOpenForm && (
        <EconomicInvestigationForm
          action={action}
          crime_reference_id={selectedIncident?.uu_id || 0}
          selectedFile={null}
          onClose={(refetch) => {
            setIsOpenForm(false);
            if (refetch) {
              fetchData();
            }
          }}
        />
      )}

      <InvestigationFileForm
        action={"Add"}
        investigationFile={undefined}
        isOpen={isOpenForm}
        onClose={() => setIsOpenForm(false)}
        incident={incidents?.find(
          (i) => i?.uu_id?.toString() === selectedIncident?.toString(),
        )}
        cr_number={""}
        isForward={true}
      />
    </div>
  );
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      submitted: "bg-yellow-100 text-yellow-800 border-yellow-200",
      assigned_to_prosecutor: "bg-red-100 text-red-800 border-red-200",
      assigned_to_clerk: "bg-blue-100 text-blue-800 border-blue-200",
      forwarded_to_coordinator:
        "bg-purple-100 text-purple-800 border-purple-200",
      completed: "bg-green-100 text-green-800 border-green-200",
    };
    return statusMap[status] || "bg-gray-100 text-gray-800 border-gray-200";
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
        status,
      )}`}
    >
      {status}
    </span>
  );
};

export default InvestigationList;
